import { useRef, useState } from "react";
import { apiPcf, type OpcaoCliente, type OpcaoProduto } from "../lib/api";
import type { PcfFormModo } from "./PcfForm";
import { Typeahead } from "../components/Typeahead";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { mascaraDecimal } from "../lib/decimal";

// atcadpcf / nova-solicitacao "Nova PCF por Demanda": cliente + produto + comprimento
// + quantidade + data → abre a TELA-PCF como DEMANDA. Fiel a MOVER/botao-gravar.

interface Props {
  usuario: Usuario;
  onVoltar: () => void;
  onAbrirPcf: (modo: PcfFormModo) => void;
}

// Máscaras do form (16/10/2026): Comprimento ZZZ.ZZZ,99 (2 casas) e Quantidade ZZZZZZZZ (inteiro).
const parseDec = (s: string) => Number(s.replace(",", ".")) || 0;

export function NovaDemanda({ usuario, onVoltar, onAbrirPcf }: Props) {
  const { theme, toggle } = useTheme();
  const [codcli, setCodcli] = useState(0);
  const [nomcli, setNomcli] = useState("");
  const [codpro, setCodpro] = useState(0);
  const [nompro, setNompro] = useState("");
  const [comprimento, setComprimento] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [datent, setDatent] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  function enterTab(e: React.KeyboardEvent) {
    if (e.key !== "Enter") return;
    const alvo = e.target as HTMLElement;
    if (alvo.tagName === "TEXTAREA") return;
    e.preventDefault();
    const foc = Array.from(
      formRef.current?.querySelectorAll<HTMLElement>("input,select,button") ?? []
    ).filter((x) => !(x as HTMLInputElement).disabled && x.tabIndex !== -1 && x.offsetParent !== null);
    const i = foc.indexOf(alvo);
    if (i >= 0 && i < foc.length - 1) foc[i + 1]?.focus();
  }

  function gravar() {
    setAviso(null);
    const qtd = parseDec(quantidade);
    const comp = parseDec(comprimento);
    if (codcli === 0) return setAviso("Cliente não informado");
    if (codpro === 0) return setAviso("Produto de produção não informado");
    if (qtd === 0) return setAviso("Quantidade de produto não informada!");
    if (comp === 0) return setAviso("Comprimento não informado!");
    if (!datent) return setAviso("Data não informada!");
    onAbrirPcf({
      kind: "nova",
      tipo: "DEMANDA",
      numped: 0,
      sequen: 0,
      codcli,
      codpro,
      qtd,
      comprimento: comp,
      datent,
    });
  }

  function limpar() {
    setCodcli(0);
    setNomcli("");
    setCodpro(0);
    setNompro("");
    setComprimento("");
    setQuantidade("");
    setDatent("");
    setAviso(null);
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            PCP - Ordem sem Pedido de Venda
            <small>Plano de Controle de Fabricação (PCF)</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onVoltar}>
          ← PCP
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <MsgModal texto={aviso} tipo="aviso" onClose={() => setAviso(null)} />

      <div className="pcf-form" ref={formRef}>
        <section className="pcf-sec">
          <h2>Dados da Demanda</h2>
          <div className="pcf-grid">
            <label className="pcf-campo pcf-lg">
              <span>Cliente</span>
              <Typeahead<OpcaoCliente>
                placeholder="Nome ou código do cliente…"
                valorInicial={nomcli}
                buscar={apiPcf.buscarCliente}
                rotulo={(c) => `${c.codigo} — ${c.nome}`}
                onEscolher={(c) => {
                  setCodcli(c.codigo);
                  setNomcli(`${c.codigo} — ${c.nome}`);
                }}
                dataCampo="cliente"
              />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Produto</span>
              <Typeahead<OpcaoProduto>
                placeholder="Nome ou código do produto…"
                valorInicial={nompro}
                buscar={apiPcf.buscarProduto}
                rotulo={(p) => `${p.codpro} — ${p.nompro}`}
                onEscolher={(p) => {
                  setCodpro(p.codpro);
                  setNompro(`${p.codpro} — ${p.nompro}`);
                }}
                dataCampo="produto"
              />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Comprimento</span>
              <input
                className="num"
                inputMode="decimal"
                value={comprimento}
                onChange={(e) => setComprimento(mascaraDecimal(e.target.value, 2))}
                onKeyDown={enterTab}
              />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Quantidade</span>
              <input
                className="num"
                inputMode="numeric"
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value.replace(/\D/g, "").slice(0, 8))}
                onKeyDown={enterTab}
              />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Previsão de Entrega</span>
              <input type="date" value={datent} onChange={(e) => setDatent(e.target.value)} onKeyDown={enterTab} />
            </label>
          </div>
        </section>

        <div className="pcf-acoes">
          <button className="btn-acao" onClick={gravar}>
            Gravar
          </button>
          <button className="btn-secundario" onClick={limpar}>
            Limpar
          </button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
